/* =============================================================================
   Interface locale
   -----------------------------------------------------------------------------
   Two languages and only two. The product is Brazilian, so `pt-BR` is a first
   class locale rather than a translation bolted on afterwards — and the union is
   deliberately narrower than BCP-47, because a locale the app cannot render is a
   selector entry that produces an English page, which is worse than not offering
   it at all.

   Formatting is a SEPARATE decision from translation, which is why `formatLocale`
   sits beside `value` rather than being derived from it: `Intl` wants a full tag
   (`en-US`, and so `$ 2,000.00`), while the interface only has two languages to
   choose between. Keeping the two apart is what lets the union stay small and
   the formatting tag stay exact.

   The flag is decoration and nothing else — it names a country, not a language,
   and it is unreadable to a screen reader. Every surface built from these options
   carries `label` as its accessible name.
   ============================================================================= */

export type Locale = 'en' | 'pt-BR';

/** What the interface speaks when nothing says otherwise, including no provider. */
export const DEFAULT_LOCALE: Locale = 'en';

export interface LocaleOption {
  value: Locale;
  /**
   * The language's name IN that language. A selector never translates its own
   * options: a reader who cannot read the current language has to be able to
   * recognise theirs, and "Português (Brasil)" is findable where "Portuguese
   * (Brazil)" is not.
   */
  label: string;
  /** Decorative. The label is the accessible name. */
  flag: string;
  /** The BCP-47 tag handed to `Intl`. */
  formatLocale: string;
}

/**
 * Keyed by the union rather than held in an array, so a lookup by locale cannot
 * come back undefined and no call site needs a non-null assertion to say so.
 */
export const LOCALE_OPTIONS: Readonly<Record<Locale, LocaleOption>> = {
  en: { value: 'en', label: 'English', flag: '🇺🇸', formatLocale: 'en-US' },
  'pt-BR': { value: 'pt-BR', label: 'Português (Brasil)', flag: '🇧🇷', formatLocale: 'pt-BR' },
};

/** The same options in the order a menu shows them. */
export const LOCALES: readonly LocaleOption[] = [LOCALE_OPTIONS.en, LOCALE_OPTIONS['pt-BR']];

export const LOCALE_STORAGE_KEY = 'escrowbuild:locale';

export function isLocale(value: unknown): value is Locale {
  return value === 'en' || value === 'pt-BR';
}

/**
 * The reader's stored choice, or `null` when they have never made one.
 *
 * `null` rather than a default, because "no choice" and "chose English" are
 * different facts: only the first should fall through to the browser's own
 * preference, and returning `en` here would anchor every browser to English.
 */
export function readStoredLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    // Storage can throw in a private window or when site data is blocked.
    return null;
  }
}

export function storeLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // A failed write must never break the switch — the locale still applies.
  }
}

/**
 * The browser's own preference, read only until the reader chooses.
 *
 * `navigator.languages` is read first and in order, because it is the reader's
 * own ranking: a browser carrying `pt-BR` above `en-US` is a Portuguese reader
 * who tolerates English, and honouring the first entry is what gets them their
 * own language. Any tag whose primary subtag is `pt` maps to `pt-BR` — `pt`,
 * `pt-PT` and `pt-BR` all describe a reader this app serves in Brazilian
 * Portuguese, which is the only Portuguese it has.
 */
export function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;

  const tags = navigator.languages.length > 0 ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    if (tag.split('-')[0].toLowerCase() === 'pt') return 'pt-BR';
  }
  return DEFAULT_LOCALE;
}

/* -----------------------------------------------------------------------------
   The active locale, for the formatting helpers
   -------------------------------------------------------------------------- */

/**
 * The tag `formatMoney` and its siblings resolve to when a caller names none.
 *
 * Held at module scope because formatting happens DURING render — a component
 * calls `formatMoney` in its own body, before any effect could have run — so the
 * provider writes this while it renders rather than from an effect. An effect
 * would leave a `pt-BR` reader's first paint showing `$ 2,000.00` with nothing
 * scheduled to correct it.
 *
 * The render-phase write is idempotent (the same locale on every render of the
 * same provider), which is what makes it safe under StrictMode's double render.
 * `null` means "no provider", and is the state the design system's own stories
 * and showcase run in — there, formatting follows the browser as it always has.
 *
 * ponytail: one provider, written during render. React's own rule is that a
 * render body does not touch anything outside it, and the ceiling here is a
 * render that is discarded rather than committed — an interrupted transition
 * would leave this at the locale of the abandoned pass until the next commit.
 * The app mounts exactly one provider at its root and changes the locale only
 * from a user's click, so the exposed window is a paint with no committed
 * difference; a second provider, or a locale driven by an async transition,
 * is when this becomes a context read like any other value instead.
 */
let activeFormatLocale: string | null = null;

export function setActiveLocale(locale: Locale): void {
  activeFormatLocale = LOCALE_OPTIONS[locale].formatLocale;
}

/** Clears the active tag, but only while it is still this locale's own. */
export function clearActiveLocale(locale: Locale): void {
  if (activeFormatLocale === LOCALE_OPTIONS[locale].formatLocale) activeFormatLocale = null;
}

export function activeLocale(): string | null {
  return activeFormatLocale;
}

/**
 * Publishes the language to the document.
 *
 * `lang` on `<html>` is what tells a screen reader which language to pronounce
 * the page in and what tells a search engine which audience it is for — without
 * it, a Portuguese page is read aloud with English phonetics. It is written from
 * the same render-phase block as the formatting tag, and for the same reason:
 * the value has to be right for the frame being rendered, not one paint later.
 */
export function applyLocaleToDocument(locale: Locale): void {
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

/**
 * The script that publishes the language BEFORE first paint, kept as a string so
 * `index.html` and the provider can never drift apart — the same contract, and
 * the same shape, as the theme's `NO_FLASH_SCRIPT`.
 *
 * `<html lang>` is what a screen reader picks a pronunciation from and what a
 * crawler reads the page as, so an English `lang` on a Brazilian page is wrong
 * for every consumer that never runs the script: a reader with JavaScript off, a
 * crawler, and the first frame of everyone else. The provider corrects it at the
 * first commit; this corrects it before there is a page to get it wrong.
 */
export const NO_FLASH_LOCALE_SCRIPT = `(function(){try{var t=navigator.languages&&navigator.languages.length?navigator.languages:[navigator.language];var p=false;for(var i=0;i<t.length;i++){if(String(t[i]).split('-')[0].toLowerCase()==='pt'){p=true;break;}}var v=localStorage.getItem('${LOCALE_STORAGE_KEY}');document.documentElement.lang=v==='en'||v==='pt-BR'?v:(p?'pt-BR':'en');}catch(e){}})();`;
